class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      const target = event.target;

      // Show loading state immediately for any filter change
      const collection = document.getElementById('ProductGridContainer')?.querySelector('.collection');
      if (collection) collection.classList.add('loading');

      if (target.type === 'checkbox' || target.type === 'radio') {
        // Checkboxes and radios: submit immediately (discrete click).
        // The 500ms debounce is only needed for text inputs and range
        // sliders where the user may still be typing or dragging.
        this.onSubmitHandler(event);
      } else {
        // Text/range inputs: debounce (user may still be typing/sliding)
        this.debouncedOnSubmit(event);
      }
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        // Parse once, reuse everywhere
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html: parsedHTML, url }];
        FacetFiltersForm.renderFilters(parsedHTML, event);
        FacetFiltersForm.renderProductGridContainer(parsedHTML);
        FacetFiltersForm.renderProductCount(parsedHTML);
        FacetFiltersForm.renderActiveFilterStrip(parsedHTML);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(parsedHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const parsedHTML = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(parsedHTML, event);
    FacetFiltersForm.renderProductGridContainer(parsedHTML);
    FacetFiltersForm.renderProductCount(parsedHTML);
    FacetFiltersForm.renderActiveFilterStrip(parsedHTML);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(parsedHTML);
  }

  /**
   * Replace the mobile sticky filter strip (<nav data-es-cat-tabs>) with the
   * freshly-rendered version from the server. This contains the active-filter
   * chips that the user sees at the top of the collection page on mobile.
   *
   * Without this call, chips were frozen at initial page load and never
   * appeared after a filter change — e.g. tapping a facet in the sidebar
   * or confirming a choice in the collection-quiz left the strip empty.
   */
  static renderActiveFilterStrip(parsedHTML) {
    const source = parsedHTML.querySelector('[data-es-cat-tabs]');
    const current = document.querySelector('[data-es-cat-tabs]');
    if (!source || !current) return;
    // Replace the inner chips while preserving the outer <nav> element so
    // any event listeners bound to it (e.g. the click delegation in
    // assets/es-collection-products.js) stay attached.
    current.innerHTML = source.innerHTML;
  }

  static renderProductGridContainer(parsedHTML) {
    const sourceContainer = parsedHTML.getElementById('ProductGridContainer');
    if (sourceContainer) {
      document.getElementById('ProductGridContainer').innerHTML = sourceContainer.innerHTML;
    }

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    // Re-apply view toggle state after grid re-render
    if (typeof initViewToggle === 'function') initViewToggle();
  }

  static renderProductCount(parsedHTML) {
    const sourceCount = parsedHTML.getElementById('ProductCount');
    const count = sourceCount ? sourceCount.innerHTML : '';
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    // Defensive: also strip the loading class from .collection so the
    // grid never gets stuck in the loading state, even if some other
    // step in the render pipeline silently fails or skips a section.
    const collection = document.querySelector('#ProductGridContainer .collection');
    if (collection) collection.classList.remove('loading');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(parsedHTML, event) {
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    // Update the filter header (Wis alles link)
    const filterHeader = document.querySelector('.es-filters__header');
    const filterHeaderNew = parsedHTML.querySelector('.es-filters__header');
    if (filterHeader && filterHeaderNew) {
      filterHeader.innerHTML = filterHeaderNew.innerHTML;
    }

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `[data-facet-summary], .facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = activeFacetsElement.innerHTML;
    });

    // Update Everspring custom active filter pills
    const esActiveFilters = document.getElementById('EsActiveFiltersDesktop');
    const esActiveFiltersNew = html.getElementById('EsActiveFiltersDesktop');
    if (esActiveFilters && esActiveFiltersNew) {
      esActiveFilters.innerHTML = esActiveFiltersNew.innerHTML;
    } else if (!esActiveFiltersNew && esActiveFilters) {
      esActiveFilters.remove();
    } else if (esActiveFiltersNew && !esActiveFilters) {
      const cloned = document.importNode(esActiveFiltersNew, true);
      const insertTarget = document.querySelector('.es-collection__grid-wrapper') || document.querySelector('.es-collection__mobile-controls') || document.querySelector('.es-collection__layout');
      if (insertTarget) {
        insertTarget.insertAdjacentElement('beforebegin', cloned);
      }
    }

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const target = document.querySelector(selector);
      if (target) target.innerHTML = html.querySelector(selector).innerHTML;
    });

    // Defensive: Dawn assumes FacetFiltersFormMobile lives inside a
    // <menu-drawer> custom element and calls .bindEvents() on it.
    // In our custom theme it lives inside .es-mobile-filter-drawer
    // instead, so closest('menu-drawer') returns null and the unguarded
    // .bindEvents() call throws TypeError, killing the entire render
    // pipeline mid-flight (renderProductGridContainer + renderProductCount
    // never run, the .collection.loading class never gets removed, and
    // every filter change leaves the grid stuck in the loading overlay).
    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const menuDrawer = mobileForm && mobileForm.closest('menu-drawer');
    if (menuDrawer && typeof menuDrawer.bindEvents === 'function') {
      menuDrawer.bindEvents();
    }
  }

  static renderCounts(source, target) {
    // Support both new es- data attributes and Dawn class fallbacks
    const targetSummary = target.querySelector('[data-facet-summary]') || target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('[data-facet-summary]') || source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('[data-facet-header]') || target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('[data-facet-header]') || source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('[data-facet-wrap]') || target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('[data-facet-wrap]') || source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.es-filter__item.hidden, .facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.textInputs = this.querySelectorAll('input[type="text"]');
    this.slider = this.querySelector('.es-price-slider');

    this.textInputs.forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });

    if (this.slider) {
      this.sliderMin = this.slider.querySelector('.es-price-slider__input-min');
      this.sliderMax = this.slider.querySelector('.es-price-slider__input-max');
      this.sliderRange = this.slider.querySelector('.es-price-slider__range');

      if (this.sliderMin && this.sliderMax) {
        this.sliderMin.addEventListener('input', this.onSliderInput.bind(this, 'min'));
        this.sliderMax.addEventListener('input', this.onSliderInput.bind(this, 'max'));
        this.sliderMin.addEventListener('change', this.onSliderChange.bind(this, 'min'));
        this.sliderMax.addEventListener('change', this.onSliderChange.bind(this, 'max'));
        this.updateSliderTrack();
      }
    }

    this.setMinAndMaxValues();
  }

  onSliderInput(type) {
    const minVal = parseInt(this.sliderMin.value);
    const maxVal = parseInt(this.sliderMax.value);

    if (type === 'min' && minVal > maxVal) {
      this.sliderMin.value = maxVal;
    }
    if (type === 'max' && maxVal < minVal) {
      this.sliderMax.value = minVal;
    }

    this.updateSliderTrack();
  }

  onSliderChange(type) {
    const minVal = parseInt(this.sliderMin.value);
    const maxVal = parseInt(this.sliderMax.value);
    const rangeMax = parseInt(this.sliderMin.max);

    // Sync slider → text inputs
    const minTextInput = this.textInputs[0];
    const maxTextInput = this.textInputs[1];

    if (minTextInput) minTextInput.value = minVal === 0 ? '' : minVal;
    if (maxTextInput) maxTextInput.value = maxVal === rangeMax ? '' : maxVal;

    this.setMinAndMaxValues();

    // Trigger the form input event to apply filters
    const event = new Event('input', { bubbles: true });
    if (type === 'min' && minTextInput) {
      minTextInput.dispatchEvent(event);
    } else if (maxTextInput) {
      maxTextInput.dispatchEvent(event);
    }
  }

  updateSliderTrack() {
    if (!this.sliderRange || !this.sliderMin || !this.sliderMax) return;
    const min = parseInt(this.sliderMin.min);
    const max = parseInt(this.sliderMin.max);
    const minVal = parseInt(this.sliderMin.value);
    const maxVal = parseInt(this.sliderMax.value);
    const range = max - min || 1;

    const leftPct = ((minVal - min) / range) * 100;
    const rightPct = ((maxVal - min) / range) * 100;

    this.sliderRange.style.left = leftPct + '%';
    this.sliderRange.style.right = (100 - rightPct) + '%';
  }

  parseDisplayValue(str) {
    // Handle locale formats: "29,95" → 29.95, "1.299,95" → 1299.95
    if (!str) return 0;
    const cleaned = str.replace(/\s/g, '');
    // If both . and , exist, the last one is the decimal separator
    if (cleaned.includes(',') && cleaned.includes('.')) {
      if (cleaned.lastIndexOf(',') > cleaned.lastIndexOf('.')) {
        return parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
      }
      return parseFloat(cleaned.replace(/,/g, '')) || 0;
    }
    if (cleaned.includes(',')) {
      return parseFloat(cleaned.replace(',', '.')) || 0;
    }
    return parseFloat(cleaned) || 0;
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();

    // Sync text inputs → slider (round to nearest integer for slider)
    if (this.sliderMin && this.sliderMax) {
      const minTextInput = this.textInputs[0];
      const maxTextInput = this.textInputs[1];
      const rangeMax = parseInt(this.sliderMin.max);

      if (minTextInput) {
        this.sliderMin.value = Math.floor(this.parseDisplayValue(minTextInput.value)) || 0;
      }
      if (maxTextInput) {
        this.sliderMax.value = Math.ceil(this.parseDisplayValue(maxTextInput.value)) || rangeMax;
      }
      this.updateSliderTrack();
    }
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.textInputs;
    if (inputs.length < 2) return;
    const minInput = inputs[0];
    const maxInput = inputs[1];

    // IMPORTANT: always store data-min / data-max as PLAIN INTEGERS.
    // Storing locale-formatted strings like "799,00" breaks Number()
    // parsing in adjustToValidValues, which silently disables clamping.
    const minTyped = this.parseDisplayValue(minInput.value);
    const maxTyped = this.parseDisplayValue(maxInput.value);
    // maxInput.data-max is the HARD upper bound from the template and is
    // never overwritten — it's the fallback for minInput.data-max when
    // maxInput is empty.
    const hardMax = this.parseDisplayValue(maxInput.getAttribute('data-max'));

    if (maxInput.value) {
      minInput.setAttribute('data-max', String(Math.floor(maxTyped)));
    } else {
      minInput.setAttribute('data-max', String(Math.floor(hardMax)));
    }
    if (minInput.value) {
      maxInput.setAttribute('data-min', String(Math.floor(minTyped)));
    } else {
      maxInput.setAttribute('data-min', '0');
    }
  }

  adjustToValidValues(input) {
    // Empty means "no constraint" — don't clamp, preserve intent.
    if (input.value === '' || input.value.trim() === '') return;

    // Use parseDisplayValue (locale-aware) instead of Number() so that
    // "799,00" / "1.299,95" parse correctly in Dutch / European locales.
    const value = this.parseDisplayValue(input.value);
    const min = this.parseDisplayValue(input.getAttribute('data-min'));
    const max = this.parseDisplayValue(input.getAttribute('data-max'));

    if (isNaN(value)) return;

    if (value < min) {
      input.value = String(Math.floor(min));
    } else if (max && value > max) {
      input.value = String(Math.floor(max));
    }
  }
}

customElements.define('price-range', PriceRange);

/* ── View toggle (grid/list) ────────────────────────────────────── */
class EsViewToggle {
  constructor() {
    this.buttons = document.querySelectorAll('.es-collection__view-btn');
    this.grid = document.getElementById('product-grid');
    if (!this.buttons.length || !this.grid) return;

    this.currentView = sessionStorage.getItem('es-view') || 'grid';
    this.applyView(this.currentView);

    this.buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const view = btn.dataset.view;
        this.applyView(view);
        sessionStorage.setItem('es-view', view);
      });
    });
  }

  applyView(view) {
    this.currentView = view;
    this.buttons.forEach((btn) => {
      const isActive = btn.dataset.view === view;
      btn.classList.toggle('active', isActive);
      btn.setAttribute('aria-pressed', isActive);
    });

    if (this.grid) {
      this.grid.classList.toggle('es-collection__grid--list', view === 'list');
    }
  }
}

// Initialize view toggle on load and after AJAX filter updates
function initViewToggle() {
  new EsViewToggle();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initViewToggle);
} else {
  initViewToggle();
}

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
